import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import { ClientCreateFloorPlanRequestDrawer, ClientGalleryCreateRequestDrawer, FloorPlanRequestSource } from 'components/pages/Client/ClientGallery/ClientGalleryCreateRequestDrawer'
import { CloudUploadOutlined, MessageOutlined, ShoppingCartOutlined } from '@mui/icons-material'
import { FC, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { GRAY_600, GRAY_700, GRAY_800, GRAY_900 } from 'constants/styling/theme'
import { PropertyTab, useClientProperty } from '../_main/contexts'
import { useAcceptDocument, useGetAssignmentDocuments } from 'dataQueries'
import { useGalleryAssignment, useGalleryDeal, useGalleryProduct } from 'components/pages/Gallery/_main/contexts'

import { AssignmentDocumentDTO } from 'models/assignment'
import { AssignmentStage } from 'constants/assignment'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import { ClientAssignmentReference } from '../ClientAssignmentReference'
import { ClientGalleryDocumentFiles } from 'components/pages/Gallery/_main/ClientGallery/ClientGalleryDocumentFiles'
import { ClientGalleryDocumentTimeline } from 'components/pages/Gallery/_main/ClientGallery/ClientGalleryDocumentTimeline'
import { ClientGalleryRequestThreadList } from 'components/pages/Gallery/_main/ClientGallery/ClientGalleryRequestThreadList'
import { ClientPropertyDocumentTypeFilter } from '../ClientPropertyDocumentTypeFilter'
import { CreateRequestButton } from './CreateRequestButton.component'
import { DocumentUploadType } from 'constants/documents'
import { EmptyContent } from 'components/common/EmptyContent'
import { MUIButton } from 'components/common/MUIButton'
import { Nullable } from 'models/helpers'
import { PageContent } from 'components/common/Page/PageContent'
import { RequestThread } from 'components/common/Threads'
import { useAuth0 } from 'utils/auth'
import { useClientGallery } from 'components/pages/Gallery/_main/ClientGallery/_main'
import { useLocation } from 'react-router'
import { useNavigate } from 'react-router-dom'
import { useThreads } from 'components/common/Threads/context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

enum DocumentContentMode {
  DOCUMENTS = 'DOCUMENTS',
  REQUESTS = 'REQUESTS'
}

/**
 * Client Property Documents content.
 *
 * @example <ClientPropertyDocumentsGallery />
 */
export const ClientPropertyDocumentsGallery: FC = () => {
  const { t } = useTranslation(['gallery'])
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const threadId = searchParams.get('threadId')

  const { userPropertyWorkspaceId } = useUserData()
  const { roles } = useAuth0()

  const {
    requiresPowerOfAttorneyDocuments,
    totalRequestCountData
  } = useGalleryDeal()

  const {
    isDocumentProduct,
    isEnergyCertificateProduct,
    isFloorPlanCertificationProduct,
  } = useGalleryProduct()

  const {
    assignmentStage,
    isDocumentWithValidateDraft,
    isDocSendToClient,
    isWaitingPoADoc,
    allowClientToCreateThreads,
  } = useGalleryAssignment()

  const {
    assignmentDocumentsStatusHistoryData,
    assignmentDocumentsEstimatedDelivery,
    showDocumentStatusHistory
  } = useClientGallery()

  const {
    assignmentId,
    propertyAssignmentsData,
    propertyData,
    handleTabChange,
    setActiveTab,
    isSelectedPropertyAssignmentCancelled,
  } = useClientProperty()

  const {
    threadList,
    fileAPIOptions,
    handleOnCreateRequestThread,
    handleCloseCreateRequestDrawer,
    selectedThread,
    setSelectedThread,
    isCreateRequestDrawerOpen,
    setIsCreateRequestDrawerOpen
  } = useThreads()

  const acceptDocument = useAcceptDocument()
  const getAssignmentOutputDocuments = useGetAssignmentDocuments(assignmentId!, DocumentUploadType.OUTPUT, userPropertyWorkspaceId, roles)

  const [activeAssignmentDocumentsFilterTab, setActiveAssignmentDocumentsFilterTab] = useState(
    assignmentId ?? (propertyAssignmentsData ? propertyAssignmentsData[0].id : '')
  )
  const [selectedDoc, setSelectedDoc] = useState<Nullable<AssignmentDocumentDTO>>()

  const [selectedMode, setSelectedMode] = useState<DocumentContentMode>(DocumentContentMode.DOCUMENTS)

  const assignmentDocumentsData = useMemo(() =>
    getAssignmentOutputDocuments.data?.data || [],
    [getAssignmentOutputDocuments.data]
  )

  const isShowDefaultInProgressContent = useMemo(() => {
    if (isDocSendToClient || isWaitingPoADoc || isSelectedPropertyAssignmentCancelled) return false
    if (isDocSendToClient || isDocumentWithValidateDraft) return false
    if (!isDocSendToClient && !!assignmentDocumentsData.length) return false

    return true
  }, [assignmentDocumentsData.length, isDocSendToClient, isDocumentWithValidateDraft, isWaitingPoADoc, isSelectedPropertyAssignmentCancelled])

  const hasMessageRequests = useMemo(() => {
    return isDocumentProduct &&
      assignmentStage === AssignmentStage.WAITING_FOR_CLIENT_TO_UPLOAD_DOCUMENTS &&
      !!totalRequestCountData && totalRequestCountData > 0
  }, [assignmentStage, isDocumentProduct, totalRequestCountData])

  const handleThreadIdInUrl = useCallback((threadId?: string) => {
    const searchParams = new URLSearchParams(location.search)

    if (threadId) {
      searchParams.set('threadId', threadId)
    } else {
      searchParams.delete('threadId')
      setSelectedThread(null)
    }

    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true })
  }, [location.pathname, location.search, navigate, setSelectedThread])

  /**
   * CreateFloorPlanRequestDrawer
   */
  const [isCreateFloorPlanRequestDrawerOpen, setIsCreateFloorPlanRequestDrawerOpen] = useState(false)
  const handleCreateRequest = useCallback(() => {
    if (isFloorPlanCertificationProduct) {
      setIsCreateFloorPlanRequestDrawerOpen(true)
      return
    }

    if (isEnergyCertificateProduct && threadList.length > 0) {
      setSelectedThread(threadList[0])
      setSelectedMode(DocumentContentMode.REQUESTS)
      return
    }

    setIsCreateRequestDrawerOpen(true)
  }, [isEnergyCertificateProduct, isFloorPlanCertificationProduct, setIsCreateRequestDrawerOpen, setSelectedThread, threadList])

  const onChangeAssignment = useCallback((assignmentId: string) => {
    setActiveAssignmentDocumentsFilterTab(assignmentId)
    handleThreadIdInUrl()
    setSelectedMode(DocumentContentMode.DOCUMENTS)
  }, [handleThreadIdInUrl])

  useLayoutEffect(() => {
    if (threadId) {
      setSelectedThread(threadList.find(thread => thread.id === threadId) || null)
      setSelectedMode(DocumentContentMode.REQUESTS)
    }

    // Show the thread content if threadId is present in the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Cleared on an empty listing too - a selection kept from the previously viewed assignment would send that
    // assignment's correction reference against this one.
    setSelectedDoc(assignmentDocumentsData[0] ?? null)
  }, [assignmentDocumentsData])

  return (
    <Stack width="100%">
      <Stack width="100%" alignItems="center" justifyContent="center" >
        <PageContent>
          {/* ASSIGNMENT TYPES FILTERS */}
          {!!propertyAssignmentsData &&
            <ClientPropertyDocumentTypeFilter
              activeAssignmentDocumentsFilterTab={activeAssignmentDocumentsFilterTab}
              callback={onChangeAssignment}
            />
          }
        </PageContent>
      </Stack>

      {(getAssignmentOutputDocuments.isLoading || !assignmentId) &&
        <Stack width="100%" alignItems="center" justifyContent="center" >
          <CircularProgress
            sx={{
              position: 'absolute',
              top: '50%',
              right: 'calc(50% - 25px)',
              color: GRAY_600
            }}
            variant="indeterminate"
            size={50}
          />
        </Stack>
      }

      {!getAssignmentOutputDocuments.isLoading && assignmentId &&
        <>
          {!isDocumentWithValidateDraft &&
            <Stack width="100%" gap="0.8rem" marginTop="3.2rem" flexDirection="row" alignItems="center" justifyContent="space-between" flexWrap="wrap">
              <ClientAssignmentReference />

              {selectedMode === DocumentContentMode.DOCUMENTS && allowClientToCreateThreads &&
                <CreateRequestButton
                  labelText={t('send_feedback')}
                  disabled={!allowClientToCreateThreads}
                  onCreateRequest={() => handleCreateRequest()}
                />
              }
            </Stack>
          }
          {isDocumentWithValidateDraft &&
            <Stack width="100%" gap="0.8rem" marginTop="3.2rem" justifyContent="space-between">
              <ClientAssignmentReference />

              <Typography variant="text-lg" fontWeight={600} color={GRAY_900}>{t('client_gallery.review_draft')}</Typography>

              <Stack width="100%" flexDirection="row" justifyContent="space-between" alignItems="flex-end">
                <Box maxWidth="56rem">
                  <Typography variant="text-sm" fontWeight={400} color={GRAY_700}>{t('client_gallery.review_draft_note')}</Typography>
                </Box>

                <Stack gap="0.8rem" flexDirection="row">
                  {selectedMode === DocumentContentMode.DOCUMENTS &&
                    <>
                      <CreateRequestButton
                        labelText={t('send_feedback')}
                        disabled={!allowClientToCreateThreads}
                        onCreateRequest={() => handleCreateRequest()}
                      />

                      <MUIButton
                        type="defaultPrimary"
                        size="sm"
                        startIcon={<CheckCircleOutlineRoundedIcon />}
                        isLoading={acceptDocument.isPending}
                        onClick={() => acceptDocument.mutate({ assignmentId, workspaceId: userPropertyWorkspaceId })}
                      >
                        {t('client_gallery.validate_document')}
                      </MUIButton>
                    </>
                  }
                </Stack>
              </Stack>
            </Stack>
          }

          <Stack width="100%" marginTop="3.2rem" gap={4} display="flex" flexDirection="row" justifyContent="space-between">
            {/* Side panel */}
            {(assignmentDocumentsData.length > 0 || showDocumentStatusHistory || threadList.length > 0) &&
              <Stack gap="2rem" height="100%" flex="0 1 34rem" minWidth={0} position="sticky">

                {assignmentDocumentsData.length > 0 &&
                  <ClientGalleryDocumentFiles
                    assignmentId={assignmentId}
                    assignmentDocumentsData={assignmentDocumentsData}
                    selectedDoc={selectedDoc}
                    propertyData={propertyData}
                    setSelectedDoc={(docData) => {
                      setSelectedDoc(docData)
                      setSelectedMode(DocumentContentMode.DOCUMENTS)
                      handleThreadIdInUrl()
                    }}
                  />
                }

                {threadList.length > 0 &&
                  <ClientGalleryRequestThreadList
                    threadMessageItems={threadList}
                    selectedThread={selectedThread}
                    setSelectedThread={(threadData) => {
                      setSelectedThread(threadData)
                      setSelectedMode(DocumentContentMode.REQUESTS)
                      threadData?.id && handleThreadIdInUrl(threadData.id)
                    }}
                  />
                }

                {showDocumentStatusHistory &&
                  <ClientGalleryDocumentTimeline
                    documentStatusHistoryData={assignmentDocumentsStatusHistoryData}
                    assignmentDocumentsEstimatedDelivery={assignmentDocumentsEstimatedDelivery}
                  />
                }

              </Stack>
            }
            {/* End of Side panel */}

            {/* Main panel */}
            <Stack flex="1 1 75%">
              {/** ASSIGNMENT NOT DELIVERED TO CLIENT */}
              {isShowDefaultInProgressContent && !hasMessageRequests && !threadId && (
                <Box maxWidth="60rem" width="100%" margin="0 auto">
                  <EmptyContent
                    title={t('client_gallery.empty')}
                    icon={<ShoppingCartOutlined sx={{ color: GRAY_800, fontSize: '2.8rem' }} />}
                    button={
                      <MUIButton
                        sx={{ marginTop: '2.4rem' }}
                        onClick={() => {
                          setActiveTab(PropertyTab.ORDERS)
                          handleTabChange(PropertyTab.ORDERS)
                        }}
                      >
                        {t('client_gallery.empty_action')}
                      </MUIButton>
                    }
                  />
                </Box>
              )}

              {/** ASSIGNMENT NOT DELIVERED TO CLIENT */}
              {isSelectedPropertyAssignmentCancelled && !hasMessageRequests && !threadId && (
                <Box maxWidth="60rem" width="100%" margin="0 auto">
                  <EmptyContent
                    title={t('client_gallery.cancelled')}
                    icon={<ShoppingCartOutlined sx={{ color: GRAY_800, fontSize: '2.8rem' }} />}
                    button={
                      <MUIButton
                        sx={{ marginTop: '2.4rem' }}
                        onClick={() => {
                          setActiveTab(PropertyTab.ORDERS)
                          handleTabChange(PropertyTab.ORDERS)
                        }}
                      >
                        {t('client_gallery.cancelled_action')}
                      </MUIButton>
                    }
                  />
                </Box>
              )}

              {/** ASSIGNMENT NOT DELIVERED TO CLIENT AND HAS REQUESTS. Documents */}
              {hasMessageRequests && !threadId &&
                <Box maxWidth="60rem" width="100%" margin="0 auto">
                  <EmptyContent
                    title={t('client_gallery.available_requests.title')}
                    description={t('client_gallery.available_requests.description')}
                    icon={<MessageOutlined sx={{ color: GRAY_800, fontSize: '2.8rem' }} />}
                  />
                </Box>
              }

              {/** ASSIGNMENT WAITING FOR PoA UPLOAD. Documents */}
              {isDocumentProduct && isWaitingPoADoc && requiresPowerOfAttorneyDocuments &&
                <Box maxWidth="60rem" width="100%" margin="0 auto">
                  <EmptyContent
                    title={t('client_gallery.empty_waiting_power_of_attorney')}
                    description={t('client_gallery.empty_waiting_power_of_attorney_description')}
                    icon={<CloudUploadOutlined sx={{ color: GRAY_800, fontSize: '2.8rem' }} />}
                    button={
                      <MUIButton
                        sx={{ marginTop: '2.4rem' }}
                        onClick={() => {
                          setActiveTab(PropertyTab.ORDERS)
                          handleTabChange(PropertyTab.ORDERS)
                        }}
                      >
                        {t('client_gallery.empty_waiting_power_of_attorney_action')}
                      </MUIButton>
                    }
                  />
                </Box>
              }

              {assignmentDocumentsData.length > 0 && selectedMode === DocumentContentMode.DOCUMENTS &&
                <iframe
                  src={selectedDoc?.signedUrl.signedURL}
                  width="100%"
                  height="1200px"
                  style={{ border: 'none' }}
                  title={t('pdf_preview')}
                />
              }

              {selectedThread && selectedMode === DocumentContentMode.REQUESTS &&
                <RequestThread
                  assignmentId={assignmentId}
                  title={t('threads:request_supplier_title')}
                  threadMessageItems={[selectedThread]}
                  fileUploadOptions={fileAPIOptions}
                  onHeaderButtonClick={handleOnCreateRequestThread}
                />
              }
            </Stack>
            {/* End of Main panel */}
          </Stack>
        </>
      }

      {/* Message thread drawer */}
      {!isFloorPlanCertificationProduct &&
        <ClientGalleryCreateRequestDrawer
          isOpen={isCreateRequestDrawerOpen}
          onClose={handleCloseCreateRequestDrawer}
          allowUploadOfDocuments
        />
      }

      {isFloorPlanCertificationProduct &&
        <ClientCreateFloorPlanRequestDrawer
          isOpen={isCreateFloorPlanRequestDrawerOpen}
          onClose={() => setIsCreateFloorPlanRequestDrawerOpen(false)}
          source={FloorPlanRequestSource.PROPERTY_DOCUMENTS}
          externalReference={selectedDoc?.signedUrl.filename ?? ''}
          correctionReference={selectedDoc?.correctionReference}
        />
      }
      {/* End of Message thread drawer */}
    </Stack>
  )
}
